import { useWidget } from '@telescope/cassini-hooks';
import { AppShell } from 'components';
import { AuthRoutes, useAuth } from 'features';
import { GeoBlocking } from 'features/geo-blocking';
import { Open } from 'generated-types/widget';
import { useEffect } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
import { SnapshotType } from 'types';
import { lazyImport } from 'utils';

const { BracketVoteApp } = lazyImport(() => import('features'), 'BracketVoteApp');
const { WinnerPage } = lazyImport(() => import('features'), 'WinnerPage');
const { ClosedPage } = lazyImport(() => import('features'), 'ClosedPage');
const { ErrorPage } = lazyImport(() => import('features'), 'ErrorPage');

// Returns the root level view based on the currently live snapshot type
const CurrentSnapshot = (props: { type: SnapshotType }) => {
  switch (props.type) {
    case SnapshotType.OPEN:
      return <BracketVoteApp />;
    case SnapshotType.WINNER:
      return <WinnerPage />;
    case SnapshotType.CLOSED:
      return <ClosedPage />;
    default:
      return null;
  }
};

export const AppRoutes = () => {
  const location = useLocation();
  const state = location.state as {
    backgroundLocation?: Location;
  };
  const { data } = useWidget<Open>({ select: (data) => data.snapshot });
  const {
    data: { settings: widgetSettings },
  } = useWidget<Open>({ select: (data) => data.widget });
  const {
    general: { geo },
  } = widgetSettings;
  const { refetchUser } = useAuth();

  // Refetch user vote history when snapshot changes
  useEffect(() => {
    refetchUser();
  }, [data.type, refetchUser]);

  const element = useRoutes(
    [
      {
        path: '/',
        element: <AppShell />,
        children: [
          {
            index: true,
            element: (
              <GeoBlocking listType={geo.listType} countries={geo.countries} allowAprUsers={geo.allowAprUsers}>
                <CurrentSnapshot type={data.type as SnapshotType} />
              </GeoBlocking>
            ),
          },
          {
            path: 'error/:errorType',
            element: <ErrorPage />,
          },
          {
            path: '*',
            element: <Navigate to="/" replace />,
          },
        ],
      },
    ],
    state?.backgroundLocation || location
  );

  return (
    <>
      {element}
      <AuthRoutes isEnabled={data.type === SnapshotType.OPEN} />
    </>
  );
};
